import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Minus, ArrowCounterClockwise, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { logger } from "@/lib/logger";

const ENTRY_TYPES = [
  { key: "general", label: "General", color: "#007AFF" },
  { key: "infantil", label: "Infantil", color: "#22C55E" },
  { key: "invitacion", label: "Invitación", color: "#A855F7" },
  { key: "prensa", label: "Prensa", color: "#06B6D4" },
  { key: "directiva", label: "Directiva", color: "#EAB308" },
  { key: "patrocinador", label: "Patrocinador", color: "#EC4899" },
  { key: "jugadores_cantera", label: "Jugadores y Cantera", color: "#14B8A6" },
];

export default function Portero() {
  const [match, setMatch] = useState(null);
  const [aforo, setAforo] = useState(null);
  const [lastAction, setLastAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const loadAforo = async () => {
    try {
      const { data } = await api.get("/access/aforo");
      setAforo(data);
      setMatch(data.match);
    } catch (e) { logger.warn("Aforo load:", e?.message); }
  };

  useEffect(() => {
    loadAforo();
    const id = setInterval(loadAforo, 3000);
    return () => clearInterval(id);
    // loadAforo estable (no depende de estado); intervalo fijo → deps vacías OK
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async (tipo) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/access/general", { tipo, direction: "in" });
      setLastAction({ type: "in", tipo, id: data.id });
      toast.success(`+1 ${tipo}`);
      loadAforo();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const exit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/access/general", { tipo: "general", direction: "out" });
      setLastAction({ type: "out", tipo: "salida" });
      toast.success("Salida registrada");
      loadAforo();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (busy) return;
    if (!window.confirm("¿Deshacer último movimiento?")) return;
    setBusy(true);
    try {
      await api.post("/access/undo");
      toast.success("Movimiento deshecho");
      setLastAction(null);
      loadAforo();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!match) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Warning size={48} className="text-[#F97316] mb-4" />
        <h2 className="font-display font-bold text-3xl uppercase">Sin Partido Activo</h2>
        <p className="text-slate-500 mt-2">Debes activar un partido antes de registrar entradas.</p>
        <button onClick={() => navigate("/partidos")} className="mt-6 bg-[#007AFF] px-5 h-11 font-display font-bold uppercase tracking-wide" style={{ borderRadius: 6 }}>
          Ir a Partidos
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header counter */}
      <div className="bg-white border border-slate-200 p-6 grid grid-cols-2 sm:grid-cols-4 gap-4" style={{ borderRadius: 6 }}>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Dentro</div>
          <div className="font-display font-bold text-5xl text-[#22C55E] tracking-tighter" data-testid="aforo-dentro">{aforo?.dentro ?? 0}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Aforo Máx</div>
          <div className="font-display font-bold text-5xl tracking-tighter">{aforo?.aforo_maximo}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Ocupación</div>
          <div className="font-display font-bold text-5xl text-[#007AFF] tracking-tighter">{aforo?.ocupacion ?? 0}%</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Partido</div>
          <div className="font-display font-bold text-xl uppercase mt-2">vs {match.rival}</div>
        </div>
      </div>

      {/* Big buttons grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {ENTRY_TYPES.map((t) => (
          <button
            key={t.key}
            data-testid={`portero-add-${t.key}`}
            onClick={() => add(t.key)}
            disabled={busy}
            className="relative bg-white hover:bg-slate-100 border-2 min-h-[140px] sm:min-h-[160px] flex flex-col items-center justify-center gap-2 transition-all active:scale-[0.97] disabled:opacity-50"
            style={{ borderRadius: 6, borderColor: t.color + "60" }}
          >
            <Plus size={36} weight="bold" style={{ color: t.color }} />
            <span className="font-display font-bold text-2xl sm:text-3xl uppercase tracking-tighter">{t.label}</span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">
              {aforo?.by_tipo?.[t.key] ?? 0} registrados
            </span>
          </button>
        ))}
      </div>

      {/* Exit + Undo */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <button
          data-testid="portero-exit"
          onClick={exit}
          disabled={busy}
          className="bg-[#EF4444]/10 hover:bg-[#EF4444]/20 border-2 border-[#EF4444]/60 min-h-[120px] flex flex-col items-center justify-center gap-2 transition active:scale-[0.97] disabled:opacity-50"
          style={{ borderRadius: 6 }}
        >
          <Minus size={32} weight="bold" className="text-[#EF4444]" />
          <span className="font-display font-bold text-2xl sm:text-3xl uppercase tracking-tighter text-[#EF4444]">Salida</span>
        </button>
        <button
          data-testid="portero-undo"
          onClick={undo}
          disabled={busy}
          className="bg-[#F97316]/10 hover:bg-[#F97316]/20 border-2 border-[#F97316]/60 min-h-[120px] flex flex-col items-center justify-center gap-2 transition active:scale-[0.97] disabled:opacity-50"
          style={{ borderRadius: 6 }}
        >
          <ArrowCounterClockwise size={32} weight="bold" className="text-[#F97316]" />
          <span className="font-display font-bold text-2xl sm:text-3xl uppercase tracking-tighter text-[#F97316]">Corregir</span>
        </button>
      </div>
    </div>
  );
}
